import _ = require('underscore');
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ContentMapper } from '~src/spacesPlatform/models/mappers/ContentMapper';

import { MapperHelper } from '../../../spacesPlatform/models/mappers/MapperHelper';
import {
    ICatalogUserInfo,
    IDataQualityWarningAuthorInfo,
    IDataQualityWarningInfo,
    IDownstreamDatasourcesConnectionInfo,
    IDownstreamWorkbooksConnectionInfo,
    INamedItemInfo,
    ITableColumnInfo,
    ITableColumnsConnectionInfo,
    ITableDatabaseInfo,
    ITagInfo
} from '../../../spacesPlatform/models/resourceInfoParts';
import { ICatalogUserItem, IDataQualityIndicator, ITableDatabaseItem } from '../../../spacesPlatform/models/resourceParts';
import { IUserInfo } from '../../../userManagement/users/models/IUserInfo';
import { IMaxDownstreamDatasources, IMaxDownstreamWorkbooks } from '../../serverApi/types/IMaxDownstreamConnections';
import { ITable } from '../../serverApi/types/ITable';
import { ITableInfo } from '../ITableInfo';

export module TableMapper {

    const M = MapperHelper;

    export function map(
        serverApiTables: ITable[],
        containers: Record<string, INamedItemInfo>,
        maxDownstreamDatasources: IMaxDownstreamDatasources,
        maxDownstreamWorkbooks: IMaxDownstreamWorkbooks,
      ): ITableInfo[] {
          const tablesToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.table, containers);
          return _.map(serverApiTables, (table: ITable) => {
            const dbInfo: ITableInfo = {
                __typename:                         'DatabaseTable',
                certifier:                          mapCertifier(table.certifier),
                certificationNote:                  table.certificationNote,
                contact:                            mapContact(table.contact),
                containerId:                        getContainerId(table.containerId, table.database),
                containerName:                      mapLocationName(table.id, table.database, tablesToProject),
                contentType:                        'table',
                createdAt:                          M.toDate(table.createdAt),
                dataQualityWarnings:                mapDataQualityWarning(table),
                dataQualityCertifications:          mapDataQualityCertification(table),
                description:                        table.description,
                downstreamWorkbooksConnection:      mapDownstreamWorkbooks(table),
                downstreamDatasourcesConnection:    mapDownstreamDatasources(table),
                maxDownstreamDatasources:           maxDownstreamDatasources ? maxDownstreamDatasources.downstreamDatasourcesConnection.totalCount : 0,
                maxDownstreamWorkbooks:             maxDownstreamWorkbooks ? maxDownstreamWorkbooks.downstreamWorkbooksConnection.totalCount : 0,
                id:                                 table.graphQLId || '',
                isCertified:                        table.isCertified,
                isEmbedded:                         table.database.isEmbedded,
                isGroupedParent:                    table.isGroupedParent,
                luid:                               table.luid,
                name:                               table.name,
                site:                               {id: table.siteId, luid: table.siteLuid, urlName: ''},
                tags:                               mapTags(table.tags),
                updatedAt:                          M.toDate(table.updatedAt),
                database:                           mapDatabase(table.database),
                columnsConnection:                  mapColumns(table),
                vizportalId:                        table.id,
                tableType:                          table.tableType
            };
            return dbInfo;
          });
    }

    function getContainerId(tableContainerId: string | null, database: ITableDatabaseItem): string {
        if(FeatureFlags.tablesInProjectsEnabled){
            if (!database.isEmbedded && tableContainerId !== undefined){
                return tableContainerId;
            }
        }
        return null;
    }

    function mapLocationName(tableId: string, database: ITableDatabaseItem, tablesToProject: Record<string, INamedItemInfo>): string {
        if (FeatureFlags.tablesInProjectsEnabled && !database.isEmbedded){
            if (tableId in tablesToProject){
                return tablesToProject[tableId].name;
            }
        }
        return null;
    }

    function mapTags(tableTags: string[]): ITagInfo[] {
        return tableTags.map(t => {
            const tag: ITagInfo = {
                __typename: 'Tag',
                name: t
            };
            return tag;
        });
    }

    function mapColumns(table: ITable): ITableColumnsConnectionInfo {
        const columns: ITableColumnsConnectionInfo = {
            totalCount: table.columns.length,
            nodes: table.columns.map(column => {
                const node: ITableColumnInfo = {
                    name: column.columnName,
                    displayName: column.columnDisplayName
                };
                return node;
            })
        };
        return columns;
    }

    function mapDatabase(tableDatabaseItem: ITableDatabaseItem): ITableDatabaseInfo {
        return tableDatabaseItem ? {
                __typename: tableDatabaseItem.dbType,
                isGrouped: tableDatabaseItem.isGroupedParent,
                hostName: tableDatabaseItem.hostName,
                name: tableDatabaseItem.name,
                isEmbedded: tableDatabaseItem.isEmbedded,
                dbType: tableDatabaseItem.dbType,
                connectionType: tableDatabaseItem.connectionType,
        } : null;
    }

    function mapDownstreamWorkbooks(table: ITable): IDownstreamWorkbooksConnectionInfo {
        const dsWorkbookConnection: IDownstreamWorkbooksConnectionInfo = {
            totalCount: table.downstreamWorkbookCount
        };
        return dsWorkbookConnection;
    }

    function mapDownstreamDatasources(table: ITable): IDownstreamDatasourcesConnectionInfo {
        const dsDatasourcesConnection: IDownstreamDatasourcesConnectionInfo = {
            totalCount: table.downstreamDataSourceCount
        };
        return dsDatasourcesConnection;
    }

    function mapDataQualityWarningOwnerToAuthor(dataQualityWarningOwner: IUserInfo | null): IDataQualityWarningAuthorInfo {
        const author: IDataQualityWarningAuthorInfo = {
            username: dataQualityWarningOwner?.username || '',
            name: dataQualityWarningOwner?.displayName || '',
            domain: dataQualityWarningOwner?.domainName || '',
            ...dataQualityWarningOwner
        };
        return author;
    }

    function mapDataQualityWarningUpdatedAt(updatedAt: string | null) {
        return updatedAt ? new Date(updatedAt) : null;
    }

    export function mapDataQualityWarning(table: ITable): IDataQualityWarningInfo[] {
        const indicators: IDataQualityIndicator[] = table.dataQualityWarning;
        return indicators.map(dqw => {
            const dataQualityWarning: IDataQualityWarningInfo = {
                author:            mapDataQualityWarningOwnerToAuthor(dqw.owner),
                owner:             mapDataQualityWarningOwnerToAuthor(dqw.owner),
                authorDisplayName: dqw.owner?.displayName || '',
                isActive:          dqw.isActive,
                isSevere:          dqw.isSevere,
                message:           dqw.message,
                vizportalId:       dqw.id,
                warningType:       dqw.type,
                updatedAt:         mapDataQualityWarningUpdatedAt(dqw.updatedAt),
                assetUrl:          (url: string) => { return table.luid; },
                assetName:         table.name,
                assetType:         table.type
            };

            return dataQualityWarning;
        });
    }

    function mapDataQualityCertification(table: ITable): IDataQualityWarningInfo[] {
      const indicators: IDataQualityIndicator[] = table.dataQualityCertifications;
      if(!indicators) {
        return [];
      }
      return indicators.map(dqc => {
          const dataQualityCertification: IDataQualityWarningInfo = {
              author:            mapDataQualityWarningOwnerToAuthor(dqc.owner),
              owner:             mapDataQualityWarningOwnerToAuthor(dqc.owner),
              authorDisplayName: dqc.owner?.displayName || '',
              isActive:          dqc.isActive,
              isSevere:          dqc.isSevere,
              message:           dqc.message,
              vizportalId:       dqc.id,
              warningType:       dqc.type,
              updatedAt:         mapDataQualityWarningUpdatedAt(dqc.updatedAt),
              assetUrl:          (url: string) => { return table.luid;},
              assetName:         table.name,
              assetType:         table.type
          };

          return dataQualityCertification;
      });
  }

    export function mapContact(contact: ICatalogUserItem): ICatalogUserInfo {
        return contact ? {
            id: contact.id,
            readOnly: contact.readOnly,
            username: contact.username,
            name: contact.displayName,
            domain: contact.domainName,
            hasServerDisplay: contact.hasServerDisplay
        } : null;
    }

    function mapCertifier(certifier: ICatalogUserItem | null): ICatalogUserInfo {
        return certifier ? {
            id: certifier.id,
            name: certifier.displayName,
            username: certifier.username,
            domain: certifier.domainName,
            readOnly: !!certifier.readOnly,
            hasServerDisplay: certifier.hasServerDisplay
        } : null;
    }
}